import * as React from 'react'
import ReactDOM from 'react-dom'
import mapboxgl from 'mapbox-gl'
import { useMap } from './MapContext'

// Space kept between the pop-up and the edge of the map when the map slides to fit it.
const EDGE_MARGIN = 8

// How far to move the pop-up along one axis so that [start, end] sits inside [min, max].
// When it cannot fit, the start (left or top) edge wins so the heading stays in view.
const shiftToFit = (start: number, end: number, min: number, max: number) => {
  if (start < min) return min - start
  if (end > max) return Math.max(max - end, min - start)
  return 0
}

// Slides the map just far enough that the whole pop-up is inside it. The pop-up keeps its
// anchor, so it still opens above its pin and points at it. While the map is moving (or after
// it slides), it waits for the move to end and checks again, in case the content changed size.
// Its stop() drops any such wait.
const keepPopupInView = (map: mapboxgl.Map, popup: mapboxgl.Popup) => {
  let onMoveEnd: (() => void) | null = null

  const waitForMoveEnd = () => {
    if (onMoveEnd) return
    const handler = () => {
      map.off('moveend', handler)
      onMoveEnd = null
      panIntoView()
    }
    onMoveEnd = handler
    map.on('moveend', handler)
  }

  const panIntoView = () => {
    if (!popup.isOpen()) return
    if (map.isMoving()) {
      waitForMoveEnd()
      return
    }
    const content = popup.getElement()?.querySelector('.mapboxgl-popup-content') || popup.getElement()
    if (!content) return
    const card = content.getBoundingClientRect()
    const box = map.getContainer().getBoundingClientRect()
    if (card.width === 0 && card.height === 0) return

    const dx = shiftToFit(card.left, card.right, box.left + EDGE_MARGIN, box.right - EDGE_MARGIN)
    const dy = shiftToFit(card.top, card.bottom, box.top + EDGE_MARGIN, box.bottom - EDGE_MARGIN)
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return

    // Moving the map by -d moves the pop-up by +d.
    waitForMoveEnd()
    map.panBy([-dx, -dy])
  }

  const stop = () => {
    if (onMoveEnd) map.off('moveend', onMoveEnd)
    onMoveEnd = null
  }

  return { panIntoView, stop }
}

interface Props {
  coordinates: [number, number]
  offset?: number | mapboxgl.PointLike | Record<string, mapboxgl.PointLike>
  anchor?: mapboxgl.Anchor
  onClose?: () => void
  children: React.ReactNode
}

const MapPopup: React.FC<Props> = ({ coordinates, offset, anchor, onClose, children }) => {
  const map = useMap()
  const containerRef = React.useRef<HTMLDivElement>(document.createElement('div'))
  const popupRef = React.useRef<mapboxgl.Popup | null>(null)
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose
  // Tracks whether popup.remove() is being called by React cleanup (coord change) vs. user action
  const removingRef = React.useRef(false)

  React.useEffect(() => {
    if (!map) return

    const popup = new mapboxgl.Popup({
      offset: offset as mapboxgl.Offset,
      anchor,
      closeButton: false,   // we render our own close button inside the card
      closeOnClick: false,  // handled manually so marker clicks don't also close the popup
      maxWidth: 'none',
    })
      .setLngLat(coordinates)
      .setDOMContent(containerRef.current)
      .addTo(map)

    // Only call onClose for user-initiated closes, not React cleanup removes
    popup.on('close', () => {
      if (!removingRef.current) onCloseRef.current?.()
    })
    popupRef.current = popup

    const fit = keepPopupInView(map, popup)
    fit.panIntoView()
    // The content can grow after opening (details or a photo loading), so check again then.
    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(() => fit.panIntoView()) : null
    resizeObserver?.observe(containerRef.current)

    return () => {
      resizeObserver?.disconnect()
      fit.stop()
      removingRef.current = true
      popup.remove()
      removingRef.current = false
      popupRef.current = null
    }
  }, [map, coordinates, offset, anchor])

  return ReactDOM.createPortal(children, containerRef.current)
}

export default MapPopup
